import Colors from '@constants/Colors';
import { Box, Button, ButtonBase, IconButton, LinearProgress, linearProgressClasses, Stack, styled, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import PreviewImage from './PreviewImage';
import PreviewOrDownloadFileButton from './PreviewOrDownloadFileButton';

type BaseInputProps = {
    containerStyle?: any;
    textStyle?: any;
    color?: string;
    control: Control<any>;
    defaultValue?: any;
    helpText?: string;
    iconFamily?: any;
    iconRight?: any;
    iconRightStyle?: any;
    iconSize?: any;
    inputStyle?: any;
    label?: string;
    min?: number;
    optional?: boolean;
    name: string;
    disabled?: boolean;
    fullWidth?: boolean;
    viewOnly?: boolean;
    viewText?: React.ReactNode | string;
    onChange?: (val, ...args) => void;
    onSubmitEditing?: () => void;
    placeholder?: string;
    // keyboardType?: KeyboardTypeOptions;
    placeholderTextColor?: string;
    multiline?: boolean;
    numberOfLines?: number;
    required?: boolean;
    // rules?: FieldRules;
    showErrorMessage?: boolean;
    subText?: string;
    maxLength?: number;
    autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
};

export const readableFileSize = (bytes: number) => {
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    // in french
    return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + ['octets', 'Ko', 'Mo', 'Go', 'To'][i];
};

const MAX_FILE_SIZE_MB = 10;

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 7,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: Colors.PRIMARY_PURPLE_30,
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: Colors.PRIMARY,
    },
}));

const InputStyles: Record<string, React.CSSProperties> = {
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    text: {
        fontSize: 14,
        color: Colors.NEUTRAL_BLACK,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
    helpText: {
        fontSize: 14,
        color: Colors.NEUTRAL_DARK_GRAY,
    },
    disabled: {
        color: Colors.NEUTRAL_PLACEHOLDER,
    },
    errorText: {
        fontSize: 12,
        color: Colors.ERROR_RED,
        marginTop: 4,
    },
    errorInput: {
        borderColor: Colors.ERROR_RED,
        borderWidth: 1,
    },
    input: {
        flex: 1,
        // textAlignVertical: 'center',
        backgroundColor: Colors.NEUTRAL_LIGHT_GRAY,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: Colors.NEUTRAL_BLACK,
        borderRadius: 5,
        borderColor: 'transparent',
        borderWidth: 1,
        alignItems: 'center',
        height: 44,
        // paddingHorizontal: 16,
        // paddingVertical: 12,
    },
    fileNameEllipsis: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
};

const InputDocuments = (
    props: BaseInputProps & {
        multiple?: boolean;
        maxFiles?: number;
        /** Defaults to "application/pdf, image/*" */
        accept?: string;
        file?: { _id?: string; name: string; size: number; mimetype: string; url?: string };
        files?: { _id?: string; name: string; size: number; mimetype: string; url?: string }[];
        rules?: any;
    },
) => {
    const [image, setImage] = useState(null);
    // const { isMobile } = useWindowSize();
    const [inputKey, setInputKey] = useState(Math.random());
    const [fileName, setFileName] = useState(props.file?.name);
    const [fileSize, setFileSize] = useState(props.file?.size);
    const [isDragging, setIsDragging] = useState(false);

    // Keep displayed filename/size in sync when editing (value might be a File or a persisted file object)
    useEffect(() => {
        if (props.multiple) return;
        if (props.file?.name || props.file?.size) {
            setFileName(props.file?.name);
            setFileSize(props.file?.size);
        }
    }, [props.file?.name, props.file?.size, props.multiple]);

    const accept = props.accept ?? 'application/pdf, image/*';
    return (
        <Stack style={InputStyles.container}>
            <Controller
                control={props.control}
                name={props.name}
                defaultValue={props.defaultValue ?? null}
                rules={{
                    validate: {
                        ...(props.rules ?? {}),
                        required: file => {
                            if (props.required && !file && !fileName) {
                                return '';
                            }
                            return true;
                        },
                        size: v => {
                            if (v && v.size > 1024 * 1024 * MAX_FILE_SIZE_MB) {
                                return `La taille du fichier ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo`;
                            }
                            return true;
                        },
                    },
                }}
                render={({ field: { ref, name, value, onChange }, fieldState: { error } }) => {
                    const handleDragOver = (e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!props.disabled && !props.viewOnly) {
                            setIsDragging(true);
                        }
                    };

                    const handleDragLeave = (e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragging(false);
                    };

                    const handleDrop = (e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragging(false);
                        if (props.disabled || props.viewOnly) return;

                        const droppedFiles = Array.from(e.dataTransfer.files);
                        if (droppedFiles.length === 0) return;

                        if (props.multiple) {
                            const remaining = props.maxFiles ? props.maxFiles - (value?.length ?? 0) : droppedFiles.length;
                            const filesToAdd = droppedFiles.slice(0, remaining);
                            const newValue = [...(value ?? []), ...filesToAdd];
                            onChange(newValue);
                            props.onChange && props.onChange(newValue);
                        } else {
                            const file = droppedFiles[0];
                            setFileName(file.name);
                            setFileSize(file.size);
                            onChange(file);
                            props.onChange && props.onChange(file);
                        }
                    };

                    return (
                        <>
                            <PreviewImage
                                title={image?.name}
                                src={image?.url}
                                open={!!image}
                                onPrev={
                                    props.multiple
                                        ? () => {
                                            const index = value.findIndex(file => file.name === image?.name);
                                            if (!props.files[index - 1]) {
                                                setImage({
                                                    name: props.files[value.length - 1]?.name,
                                                    url: props.files[value.length - 1]?.url,
                                                });
                                                return;
                                            }
                                            setImage({
                                                name: props.files[index - 1]?.name,
                                                url: props.files[index - 1]?.url,
                                            });
                                        }
                                        : undefined
                                }
                                onNext={
                                    props.multiple
                                        ? () => {
                                            const index = value.findIndex(file => file.name === image?.name);
                                            if (!props.files[index + 1]) {
                                                setImage({
                                                    name: props.files[0]?.name,
                                                    url: props.files[0]?.url,
                                                });
                                                return;
                                            }
                                            setImage({
                                                name: props.files[index + 1]?.name,
                                                url: props.files[index + 1]?.url,
                                            });
                                        }
                                        : undefined
                                }
                                onClose={() => {
                                    setImage(null);
                                }}
                            />
                            {(!props.multiple || (props.multiple && props.maxFiles !== value?.length)) && (
                                <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                                    <Typography>
                                        {props.label}
                                        {props.helpText && (
                                            <Typography component="span" style={InputStyles.helpText}>
                                                {' '}
                                                {props.helpText}
                                            </Typography>
                                        )}
                                    </Typography>
                                    {props.optional && !props.viewOnly && <Typography> (facultatif)</Typography>}
                                </Stack>
                            )}
                            <Stack style={{ gap: 16 }}>
                                {(!props.multiple || (props.multiple && props.maxFiles !== value?.length)) && (
                                    <div style={{ position: 'relative' }}>
                                        <Stack
                                            sx={{
                                                padding: {
                                                    xs: '6px 12px',
                                                    sm: '20px',
                                                },
                                                flexDirection: 'row',
                                                display: 'flex',
                                                overflow: 'hidden',
                                                borderRadius: '8px',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: 1,
                                                border: isDragging ? '2px dashed #7b6ef6' : '2px dashed #a1a1a1',
                                                background: isDragging ? 'rgba(75, 58, 210, 0.25)' : 'var(--Transparant-Purple, #15143b)',
                                                transition: 'border-color 0.2s, background 0.2s',
                                                cursor: props.disabled || props.viewOnly ? 'default' : 'pointer',
                                            }}
                                            component={'label'}
                                            htmlFor={'input' + name}
                                            onDragOver={handleDragOver}
                                            onDragEnter={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}>
                                            <i
                                                className="fi-br-file-upload"
                                                style={{ fontSize: 28, color: Colors.NEUTRAL_WHITE }}></i>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {!(fileName && fileSize) ? (
                                                    <Typography
                                                        style={{
                                                            color: 'var(--Neutral-White, #FFF)',
                                                            fontStyle: 'normal',
                                                            fontWeight: 400,
                                                            lineHeight: 'normal',
                                                            fontSize: '14px',
                                                        }}>
                                                        {`Glissez déposez votre fichier (pdf, jpg, jpeg, png) pour télécharger votre ${props?.label} ici.`}
                                                        <Typography
                                                            style={{
                                                                color: '#C0C0C0',
                                                                fontSize: '14px',
                                                                fontStyle: 'normal',
                                                                fontWeight: 400,
                                                                lineHeight: 'normal',
                                                            }}>
                                                            {' '}
                                                            (max. {MAX_FILE_SIZE_MB} Mo)
                                                        </Typography>
                                                    </Typography>
                                                ) : props?.file?._id ? (
                                                    <div
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}>
                                                        <PreviewOrDownloadFileButton
                                                            file={{
                                                                _id: props.file._id,
                                                                name: props.file.name,
                                                                size: props.file.size,
                                                                url: props.file.url,
                                                            }}
                                                            action="preview"
                                                            textStyle={{ color: Colors.NEUTRAL_WHITE }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <ButtonBase
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (props?.file?.mimetype?.includes('image')) {
                                                                setImage(props?.file);
                                                                return;
                                                            }

                                                            window.open(
                                                                props?.file?.url ?? URL.createObjectURL(value),
                                                                '_blank',
                                                            );
                                                        }}
                                                        sx={{ width: '100%', minWidth: 0, justifyContent: 'flex-start' }}>
                                                        <Typography style={InputStyles.fileNameEllipsis}>
                                                            {fileName}
                                                            <Typography style={{ marginTop: 8 }}>
                                                                {' '}
                                                                ({readableFileSize(fileSize)})
                                                            </Typography>
                                                        </Typography>
                                                    </ButtonBase>
                                                )}
                                            </div>
                                            {!fileName && (
                                                <IconButton
                                                    color="primary"
                                                    size="large"
                                                    disabled={props.disabled || props.viewOnly}
                                                    component="label"
                                                    htmlFor={'input' + name}
                                                    sx={{
                                                        fontWeight: 600,
                                                    }}>
                                                    <i className="fi fi-br-upload" />
                                                </IconButton>
                                            )}
                                        </Stack>
                                        {fileName && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 16,
                                                    right: 28,
                                                    cursor: 'pointer',
                                                }}>
                                                <ButtonBase
                                                    onClick={() => {
                                                        setInputKey(Math.random());
                                                        setFileName(null);
                                                        onChange(null);
                                                    }}>
                                                    <i
                                                        className="fi fi-br-trash"
                                                        style={{ fontSize: 24, color: Colors.ERROR_RED }}></i>
                                                </ButtonBase>
                                            </div>
                                        )}
                                        <input
                                            key={inputKey}
                                            type="file"
                                            style={{
                                                ...InputStyles.input,
                                                ...(error ? InputStyles.errorInput : {}),
                                                ...props.inputStyle,
                                                display: 'none',
                                            }}
                                            // allow pdf and images
                                            accept={accept}
                                            ref={ref}
                                            id={'input' + name}
                                            name={'name'}
                                            multiple={props.multiple}
                                            placeholder={props.placeholder}
                                            disabled={
                                                props.disabled || (props.multiple && props.maxFiles === value?.length)
                                            }
                                            onChange={e => {
                                                if (props.multiple) {
                                                    const arrayFiles = Array.from(e.target.files);
                                                    const newValue = [...(value ?? []), ...arrayFiles];
                                                    onChange(newValue);
                                                    props.onChange && props.onChange(newValue);
                                                } else {
                                                    setFileName(e.target.files[0]?.name);
                                                    setFileSize(e.target.files[0]?.size);
                                                    onChange(e.target.files[0]);
                                                    props.onChange && props.onChange(e.target.files[0]);
                                                }
                                            }}
                                        />
                                        {error && <span style={InputStyles.errorText}>{error.message}</span>}
                                    </div>
                                )}
                                {props.multiple && (
                                    <>
                                        {value?.map((file: any, index: number) => {
                                            return (
                                                <div key={index} style={{ position: 'relative' }}>
                                                    <Typography>
                                                        {props.label}
                                                        {props.helpText && (
                                                            <Typography component="span" style={InputStyles.helpText}>
                                                                {' '}
                                                                {props.helpText}
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    <Box
                                                        component="label"
                                                        sx={{
                                                            padding: {
                                                                xs: '6px 12px',
                                                                sm: '12px',
                                                            },
                                                            width: '100%',
                                                        }}
                                                        style={{
                                                            display: 'flex',
                                                            overflow: 'hidden',
                                                            borderRadius: '8px',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            marginTop: 8,
                                                            flexDirection: 'row',
                                                            gap: 8,

                                                            border: '2px solid #a1a1a1',
                                                            background: '#2a265e',
                                                        }}
                                                        htmlFor={name}>
                                                        <Stack
                                                            direction={'row'}
                                                            width={'100%'}
                                                            flex={1}
                                                            justifyContent="flex-start"
                                                            gap={2}
                                                            alignItems="center"
                                                            sx={{ minWidth: 0 }}>
                                                            <i
                                                                className="fi fi-br-file-upload"
                                                                style={{
                                                                    fontSize: 28,
                                                                    color: Colors.NEUTRAL_WHITE,
                                                                }}></i>
                                                            {file._id ? (
                                                                <PreviewOrDownloadFileButton
                                                                    file={{
                                                                        _id: file._id,
                                                                        name: file.name,
                                                                        size: file.size,
                                                                        url: file.url,
                                                                    }}
                                                                    action="preview"
                                                                    textStyle={{ color: Colors.NEUTRAL_WHITE }}
                                                                />
                                                            ) : (
                                                                <ButtonBase
                                                                    onClick={() => {
                                                                        if (
                                                                            (file.type ?? file.mimetype).includes('image')
                                                                        ) {
                                                                            setImage({
                                                                                name: file.name,
                                                                                url: file?.url ?? URL.createObjectURL(file),
                                                                            });
                                                                        } else {
                                                                            window.open(
                                                                                file?.url ?? URL.createObjectURL(file),
                                                                                '_blank',
                                                                            );
                                                                        }
                                                                    }}
                                                                    component={Stack}
                                                                    sx={{
                                                                        flexDirection: 'row',
                                                                        display: 'flex',
                                                                        minWidth: 0,
                                                                        maxWidth: '70%',
                                                                    }}>
                                                                    <Typography
                                                                        style={{
                                                                            ...InputStyles.fileNameEllipsis,
                                                                            fontWeight: 700,
                                                                        }}>
                                                                        {file.name}
                                                                        <Typography
                                                                            component="small"
                                                                            style={{
                                                                                fontWeight: 'normal',
                                                                                marginTop: 8,
                                                                                color: Colors.GREY,
                                                                            }}>
                                                                            {' '}
                                                                            ({readableFileSize(file.size)})
                                                                        </Typography>
                                                                    </Typography>
                                                                </ButtonBase>
                                                            )}
                                                            <div style={{ flexGrow: 1 }}></div>
                                                            <ButtonBase
                                                                onClick={() => {
                                                                    setInputKey(Math.random());
                                                                    setFileName(null);
                                                                    onChange(value.filter((_, i) => i !== index));
                                                                }}>
                                                                <i
                                                                    className="fi fi-br-trash"
                                                                    style={{ fontSize: 24, color: Colors.WHITE }}></i>
                                                            </ButtonBase>
                                                        </Stack>
                                                        {file?.uploadProgress > 0 && (
                                                            <Stack style={{ width: '100%' }}>
                                                                <BorderLinearProgress
                                                                    variant="determinate"
                                                                    value={file?.uploadProgress}
                                                                />
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Stack>
                        </>
                    );
                }}
            />
        </Stack>
    );
};

export default InputDocuments;
